export enum IntegerRangeSchemaValues {
    GREATER_OR_EQUAL = 'greater_or_equal',
    GREATER_THAN = 'greater_than',
    LESS_OR_EQUAL = 'less_or_equal',
    LESS_THAN = 'less_than',
}

const parseInputToRange = (val: any): number[] => {
    if (typeof val !== 'string') return []

    const normalized = val.trim().replace(/\s*-\s*/g, '-')

    if (/^\d+$/.test(normalized)) {
        return [Number(normalized)]
    }

    if (/^\d+-\d+$/.test(normalized)) {
        const [start, end] = normalized.split('-').map(Number)
        if (start >= end) return [] // Invalid range, start must be less than end

        if (end - start >= 10000)  return [] // Range exceeds 10,000 elements

        const range: number[] = []
        for (let i = start; i <= end; i++) range.push(i)
        return range
    }

    return []
}

export const getIntegerRangeSchema = (inputString: any, schema: any) => {
    const baseTest = (
        name: string,
        message: string,
        testFn: (n: any) => boolean
    ) =>
        inputString.test(name, message, (val: any) => {
            const values = parseInputToRange(val)
            if (!values.length) return false
            if (!schema.is_required && (val === 'null' || val === null))
                return true
            return values.every(testFn)
        })

    if (typeof schema.greater_or_equal !== 'undefined') {
        inputString = baseTest(
            IntegerRangeSchemaValues.GREATER_OR_EQUAL,
            `Each number must be ≥ ${schema.greater_or_equal}`,
            (n) =>
                n && n !== 'null' && !isNaN(n) && Number.isInteger(Number(n))
                    ? BigInt(n) >= BigInt(schema.greater_or_equal)
                    : Number(n) >= Number(schema.greater_or_equal)
        )
    }

    if (typeof schema.greater_than !== 'undefined') {
        inputString = baseTest(
            IntegerRangeSchemaValues.GREATER_THAN,
            `Each number must be > ${schema.greater_than}`,
            (n) =>
                n && n !== 'null' && !isNaN(n) && Number.isInteger(Number(n))
                    ? BigInt(n) > BigInt(schema.greater_than)
                    : Number(n) > Number(schema.greater_than)
        )
    }

    if (typeof schema.less_or_equal !== 'undefined') {
        inputString = baseTest(
            IntegerRangeSchemaValues.LESS_OR_EQUAL,
            `Each number must be ≤ ${schema.less_or_equal}`,
            (n) => {
                if (
                    n &&
                    n !== 'null' &&
                    !isNaN(n) &&
                    Number.isInteger(Number(n))
                ) {
                    return BigInt(n) <= BigInt(schema.less_or_equal)
                }
                return Number(n) <= Number(schema.less_or_equal)
            }
        )
    }

    if (typeof schema.less_than !== 'undefined') {
        inputString = baseTest(
            IntegerRangeSchemaValues.LESS_THAN,
            `Each number must be < ${schema.less_than}`,
            (n) =>
                n && n !== 'null' && !isNaN(n) && Number.isInteger(Number(n))
                    ? BigInt(n) < BigInt(schema.less_than)
                    : Number(n) < Number(schema.less_than)
        )
    }

    if (schema.is_required) {
        inputString = inputString
            .required()
            .test('valid-format', 'Must be an integer or range', (val: any) => {
                return parseInputToRange(val).length > 0
            })
            .test('max-range-size', 'Range must not exceed 10,000 elements', (val: any) => {
                return parseInputToRange(val).length <= 10000
            })
    } else {
        inputString = inputString
            .transform((_: any, val: any) => (val === '' ? null : val))
            .nullable()
            .notRequired()
            .test('valid-format', 'Must be an integer or range', (val: any) => {
                return val === null || parseInputToRange(val).length > 0
            })
    }

    return inputString
}
